/* Root Styles: The first thing since they should have the less precendence */
import '@dtvgo/font-faces';
import './index.scss';

/* React 18 deprecates findDOMNode (removed in 19); the spatial-navigation lib
   still calls it — see src/utils/findDOMNodeCompat.js. Must run before render. */
import './utils/findDOMNodeCompat';

/* Fast Object.assign for webOS 3 (Chromium 38) — core-js's shim is ~20x
   slower and runs on every focusable render (see fastObjectAssign.js). Must
   run before any provider/component code spreads objects. */
import './utils/fastObjectAssign';

/* Native-backed URL for webOS 3 (Chromium 38) — core-js's pure-JS URL costs
   ~73ms per `new URL()` on the TV, and shaka calls it on every segment request
   (see fastUrl.js). Must run before the player and the request layer. */
import './utils/fastUrl';

/* Fast Array.from for the legacy bundle — core-js's costs ~0.12ms per element
   on webOS 3 and backs every spread of a string/Set/Map (the JWT decode of the
   token check alone was 3-5s per channel join; see fastArrayFrom.js). */
import './utils/fastArrayFrom';

/* Fast String.prototype.startsWith for the legacy bundle — shaka's MPD parser
   calls it constantly and core-js's was ~7% of steady playback on a 1h channel
   (see fastStringStartsWith.js). */
import './utils/fastStringStartsWith';

/* External */
import { Provider, ReactReduxContext } from 'react-redux';
import { createRoot } from 'react-dom/client';
import { Router } from 'react-router';

/* Components */
import { App } from './app';
import ConfigProvider from './providers/ConfigProvider';
import ConnectionStatusProvider from './providers/ConnectionStatusProvider';
import CurrentTimeProvider from './providers/CurrentTimeProvider';
import CursorStateProvider from './providers/CursorStateProvider';
import DeviceInfoProvider from './providers/DeviceInfoProvider';
import InterstitialProvider from './providers/InterstitialProvider';
import IntlProviderWrapper from './providers/IntlProviderWrapper';
import KeyBoardStateProvider from './providers/KeyBoardStateProvider';
import LoadingProvider from './providers/LoadingProvider';
import RestoreFocusProvider from './providers/RestoreFocusProvider';
import { SidebarStateProvider } from './providers/SidebarStateContext';
import UserProvider from './providers/UserProvider';
import ForegroundAppProvider from './providers/ForegroundAppProvider';
import TermsOfServiceProvider from './providers/TermsOfServiceProvider';
import QueryParamsProvider from './providers/QueryParamsProvider';

/* Storage */
import store from './storage/configureStore';

/* Utils */
import { ErrorType } from './utils/constants';
import { APP_VERSION } from './utils/env';
import { initSpatialNavigation } from './utils/spatialNavigation';
import { history } from './utils/history';
import platform from './utils/platform';
import { initDatadog } from './utils/datadog';
import { reportLoadDrmAgentError } from './services/gaService';

const loaderContainer = document.querySelector('.loader-container');

const showLoading = () => loaderContainer.classList.remove('loader--hide');
const hideLoading = () => {
  loaderContainer.classList.add('loader--hide');
};

initSpatialNavigation();
platform.init((error) => {
  if (error.type === ErrorType.LOAD_DRM_AGENT) {
    reportLoadDrmAgentError(error);
  }
});

createRoot(document.getElementById('root')).render(
  <vega-app allowSystemKeyEvents="true">
    <ConfigProvider>
      <IntlProviderWrapper>
        <Provider store={store} context={ReactReduxContext}>
          <Router history={history}>
            <LoadingProvider>
              <ForegroundAppProvider>
                <CurrentTimeProvider>
                  <DeviceInfoProvider>
                    <ConnectionStatusProvider>
                      <KeyBoardStateProvider>
                        <CursorStateProvider>
                          <RestoreFocusProvider>
                            <UserProvider>
                              <TermsOfServiceProvider>
                                <InterstitialProvider>
                                  <SidebarStateProvider>
                                    <QueryParamsProvider>
                                      <App
                                        hideLoading={hideLoading}
                                        showLoading={showLoading}
                                      />
                                    </QueryParamsProvider>
                                  </SidebarStateProvider>
                                </InterstitialProvider>
                              </TermsOfServiceProvider>
                            </UserProvider>
                          </RestoreFocusProvider>
                        </CursorStateProvider>
                      </KeyBoardStateProvider>
                    </ConnectionStatusProvider>
                  </DeviceInfoProvider>
                </CurrentTimeProvider>
              </ForegroundAppProvider>
            </LoadingProvider>
          </Router>
        </Provider>
      </IntlProviderWrapper>
    </ConfigProvider>
  </vega-app>,
);

/* RUM is initialized after first paint: the SDK is a split chunk fetched only
   when VITE_IS_DATADOG_ACTIVE is on, so it never blocks boot. */
initDatadog();

async function logInfo() {
  const appIdPromise = platform.getAppId();
  const deviceInfoPromise = platform.getDeviceInfo();
  const versionAppPromise = platform.getAppVersion();

  const appId = await appIdPromise;
  const deviceInfo = await deviceInfoPromise;
  const versionId = await versionAppPromise;

  /* eslint-disable no-console -- Allow to log this info to the console */
  console.group('App & Device info');
  console.log('AppId:', appId);
  console.log('AppVersion:', APP_VERSION);
  console.log('DeviceInfo:', deviceInfo);
  console.log('VersionId:', versionId);
  console.groupEnd();
  /* eslint-enable no-console -- re-enable */
}

(function scheduleLogInfo() {
  setTimeout(logInfo, 0);
}());
